"use client";

import { useState } from "react";
import type {
  QuickPayOutput,
  QuickPayReceiptContext,
} from "@tillix/api-contract/client-schemas";
import { Button } from "@tillix/ui-atoms/button";
import { Card } from "@tillix/ui-atoms/card";
import { Separator } from "@tillix/ui-atoms/separator";
import { formatCurrency, formatDate } from "@tillix/utils/format";
import { ArrowRight, CheckCircle, Mail } from "lucide-react";
import { observer } from "mobx-react-lite";
import { useT } from "@/features/i18n/hooks/use-t";
import { FEE_BY_METHOD } from "@/features/pay/state/payment-fees";
import QuickPayRegisterForm from "./QuickPayRegisterForm";

interface QuickPayConfirmationProps {
  result: QuickPayOutput;
  amount: number;
  receiptContext: QuickPayReceiptContext | null;
  onDone: () => void;
  onRegister: (data: { email: string }) => Promise<void>;
  nameOnAccount: string;
}

function QuickPayConfirmation({
  result,
  amount,
  receiptContext,
  onDone,
  onRegister,
  nameOnAccount,
}: QuickPayConfirmationProps) {
  const { t } = useT();
  const [showRegisterForm, setShowRegisterForm] = useState(false);
  const [registered, setRegistered] = useState(false);

  const isCardPayment = receiptContext?.paymentType === "card";
  const isCheckPayment = receiptContext?.paymentType === "check";
  const fee = isCardPayment
    ? FEE_BY_METHOD.CARD
    : isCheckPayment
      ? FEE_BY_METHOD.BANK_ACCOUNT
      : 0;
  const totalPaid = amount + fee;

  const handleRegister = async (data: { email: string }) => {
    await onRegister(data);
    setRegistered(true);
  };

  return (
    <Card className="w-full max-w-md">
      {/* Success header */}
      <Card.Header className="text-center">
        <div className="mb-2 flex justify-center">
          <CheckCircle className="size-12 text-green-500" />
        </div>
        <Card.Title>{t("quickPay.paymentSubmitted")}</Card.Title>
        <Card.Description>
          {t("quickPay.paymentReceived", { amount: formatCurrency(totalPaid) })}
        </Card.Description>
      </Card.Header>

      <Card.Content className="space-y-3">
        {/* Receipt details */}
        {receiptContext && (
          <DetailRow
            label={t("quickPay.accountHash")}
            value={receiptContext.accountNumber}
          />
        )}
        <DetailRow
          label={t("quickPay.paymentAmount")}
          value={formatCurrency(amount)}
        />
        {fee > 0 && (
          <DetailRow
            label={isCardPayment ? t("quickPay.cardFee") : t("quickPay.achFee")}
            value={formatCurrency(fee)}
          />
        )}
        <div className="flex items-center justify-between font-semibold text-sm">
          <span>{t("quickPay.totalPaid")}</span>
          <span>{formatCurrency(totalPaid)}</span>
        </div>
        {receiptContext && (
          <>
            <DetailRow
              label={t("quickPay.payerName")}
              value={receiptContext.payerName}
            />
            <DetailRow
              label={`${isCardPayment ? t("quickPay.card") : t("quickPay.account")} ${t("quickPay.endingIn")}`}
              value={receiptContext.lastFour}
            />
          </>
        )}
        <DetailRow
          label={t("quickPay.transactionId")}
          value={result.transactionId}
        />
        <DetailRow
          label={t("quickPay.status")}
          value={result.statusDescription}
        />
        <DetailRow
          label={t("quickPay.date")}
          value={formatDate(new Date(result.transactionDate))}
        />

        <Separator />

        {/* Actions */}
        {!(registered || showRegisterForm) && (
          <div className="space-y-4 pt-1">
            <p className="text-center text-muted-foreground text-sm">
              {t("quickPay.createAccountPrompt")}
            </p>
            <div className="flex gap-3">
              <Button className="flex-1" onClick={onDone} variant="outline">
                {t("application.done")}
              </Button>
              <Button
                className="flex-1"
                onClick={() => setShowRegisterForm(true)}
              >
                {t("quickPay.createAccount")}
                <ArrowRight className="ml-1.5 size-4" />
              </Button>
            </div>
          </div>
        )}

        {showRegisterForm && !registered && (
          <div className="space-y-3 pt-1">
            <QuickPayRegisterForm
              defaultEmail={receiptContext?.email ?? ""}
              defaultName={nameOnAccount}
              onSubmit={handleRegister}
            />
            <Button
              className="w-full"
              onClick={() => setShowRegisterForm(false)}
              variant="ghost"
            >
              {t("application.back")}
            </Button>
          </div>
        )}

        {registered && (
          <div className="space-y-4 pt-1">
            <div className="flex flex-col items-center gap-2 rounded-xl border border-border/50 bg-muted/40 px-4 py-5 text-center">
              <div className="flex size-10 items-center justify-center rounded-full bg-success/10">
                <Mail className="size-5 text-success" />
              </div>
              <p className="font-medium text-sm">
                {t("quickPay.checkYourEmail")}
              </p>
              <p className="text-muted-foreground text-xs">
                {t("quickPay.signInLinkSent")}
              </p>
            </div>
            <Button className="w-full" onClick={onDone} variant="outline">
              {t("application.done")}
            </Button>
          </div>
        )}
      </Card.Content>
    </Card>
  );
}

function DetailRow({ label, value }: { label: string; value: string }) {
  return (
    <div className="flex items-center justify-between text-sm">
      <span className="text-muted-foreground">{label}</span>
      <span className="font-medium">{value}</span>
    </div>
  );
}

export default observer(QuickPayConfirmation);
